import { hasConfirmedTaskReceiptResult } from "./taskReceipts";
import type { TaskActionRequest as TaskRequest, TaskRecord, TaskSnapshot, TaskWriteAction } from "../../supabase/functions/_shared/taskActionDomain";
import { canonicalTaskRecord, taskSnapshotsEqual, hasTaskWriteProof } from "../../supabase/functions/_shared/taskActionDomain";

import { claimConnectorWrite, finishConnectorWrite, loadConnectorWrite, type ConnectorWriteBody, type PendingConnectorWrite } from "@/lib/connectorPendingWrite";

export type { TaskActionRequest as TaskRequest, TaskRecord, TaskSnapshot, TaskWriteAction } from "../../supabase/functions/_shared/taskActionDomain";
export const TASK_WRITE_ACTIONS = ["create", "update", "delete", "complete", "reopen", "link_event"] as const;
export const TASK_REVIEW_ACTIONS = ["create", "update", "delete", "complete", "reopen"] as const;
export type TaskAction = "search" | "get" | TaskWriteAction;
export interface TaskActionPrefill {
  action: TaskAction; action_id: string; operation_id: string;
  task_id?: string; query?: string; title?: string; priority?: "Normal" | "Important" | "Critical";
  due_date?: string | null; time_label?: string | null;
  expected_user_id?: string; expected_session_id?: string; recovery_only?: boolean;
}
export interface TaskResponse extends Record<string, unknown> {
  ok: true; action?: string; operation_id?: string; task_id?: string; task?: TaskRecord;
  tasks?: TaskRecord[]; complete?: boolean; has_more?: boolean;
  result?: Record<string, unknown>; receipt_id?: string;
}
export class TaskExecutorError extends Error {
  constructor(code: string, public context?: Response, public safe_retry = false, public confirmed_failed = false) { super(code); this.name = "TaskExecutorError"; }
}
export const taskUuid = (value: unknown): value is string => typeof value === "string" && /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(value);
const object = (value: unknown): value is Record<string, unknown> => !!value && typeof value === "object" && !Array.isArray(value);
export const taskOperationKey = (operationId: string, action: string) => "mirror_task_" + operationId + "_" + action + "_0";

/** The same captured turn resolves to the same operation after a modal remount. */
export async function taskOriginOperationId(ownerId: string, sessionId: string, actionId: string, kind = "operation") {
  const bytes = new Uint8Array(await crypto.subtle.digest("SHA-256", new TextEncoder().encode(JSON.stringify(["vyv-task", ownerId, sessionId, actionId, kind])))).slice(0, 16);
  bytes[6] = (bytes[6] & 15) | 80; bytes[8] = (bytes[8] & 63) | 128;
  const hex = Array.from(bytes, byte => byte.toString(16).padStart(2, "0")).join("");
  return [hex.slice(0, 8), hex.slice(8, 12), hex.slice(12, 16), hex.slice(16, 20), hex.slice(20)].join("-");
}

export interface MirrorTaskReceiptReference {
  action: TaskWriteAction; operation_id: string; task_id?: string;
  action_id: string; userId: string; sessionId: string;
}
export interface MirrorTaskOrigin { userId: string; sessionId: string; actionId: string }
export function matchesMirrorTaskReceiptOrigin(value: unknown, origin: MirrorTaskOrigin): value is MirrorTaskReceiptReference {
  return object(value) && taskUuid(origin.userId) && taskUuid(origin.sessionId) && /^[A-Za-z0-9._:-]{1,160}$/.test(origin.actionId) && value.userId === origin.userId &&
    value.sessionId === origin.sessionId && value.action_id === origin.actionId && taskUuid(value.operation_id) && value.operation_id !== origin.actionId &&
    TASK_REVIEW_ACTIONS.includes(value.action as typeof TASK_REVIEW_ACTIONS[number]) && (value.task_id === undefined || taskUuid(value.task_id));
}
export function mirrorTaskReceiptReference(entry: PendingConnectorWrite, origin: MirrorTaskOrigin): MirrorTaskReceiptReference | null {
  const body = entry.body;
  const value = { action: body.action, operation_id: body.operation_id, task_id: body.task_id,
    action_id: origin.actionId, userId: origin.userId, sessionId: origin.sessionId };
  if (entry.group !== "local_task" || entry.key !== origin.userId + ":local_task" || entry.ownerId !== origin.userId || entry.status !== "succeeded" ||
    !object(body.mirror_context) || body.mirror_context.session_id !== origin.sessionId || body.mirror_context.action_id !== origin.actionId ||
    body.idempotency_key !== taskOperationKey(String(body.operation_id), body.action) || !matchesMirrorTaskReceiptOrigin(value, origin)) return null;
  return value;
}

export function parseTaskRecord(value: unknown): TaskRecord | null {
  if (!object(value)) return null;
  const fields = ["id", "user_id", "title", "priority", "due_date", "time_label", "done", "created_at", "completed_at", "source", "external_id", "client_action_id"];
  try {
    const record = canonicalTaskRecord({ task: Object.fromEntries(fields.map(field => [field, value[field]])) });
    if (value.content_truncated === true) return value.writable === false && value.read_only === true && value.snapshot === null ? value as unknown as TaskRecord : null;
    if (["writable", "read_only", "content_truncated"].some(field => value[field] !== (record as unknown as Record<string, unknown>)[field]) || !taskSnapshotsEqual(value.snapshot, record.snapshot)) return null;
    return value as unknown as TaskRecord;
  } catch { return null; }
}

export function hasConfirmedTaskActionResult(body: Record<string, unknown>, value: unknown, ownerId?: string): value is TaskResponse {
  if (!object(value) || value.ok !== true || value.action !== body.action || value.operation_id !== body.operation_id ||
    !taskUuid(value.receipt_id) || value.task_id !== body.task_id || !object(value.result)) return false;
  return hasTaskWriteProof(body, value.result, ownerId);
}

/** A new preflight rejection does not establish the old attempt's outcome. */
export function isConfirmedTaskFailure(body: Record<string, unknown>, value: unknown): boolean {
  return object(value) && value.safe_retry === true && (value.receipt_status === "failed" || value.status === "failed") &&
    value.operation_id === body.operation_id && value.action === body.action && taskUuid(value.receipt_id);
}

export type TaskOwnerGuard = () => void;
const noGuard: TaskOwnerGuard = () => {};
async function checked<T>(work: () => Promise<T>, assertCurrent: TaskOwnerGuard): Promise<T> {
  assertCurrent(); const value = await work(); assertCurrent(); return value;
}
export async function taskOwnerSession(ownerId: string, assertCurrent: TaskOwnerGuard = noGuard) {
  const { supabase } = await checked(() => import("@/integrations/supabase/client"), assertCurrent);
  const user = await checked(() => supabase.auth.getUser(), assertCurrent);
  if (user.error || user.data.user?.id !== ownerId) throw new TaskExecutorError("invalid_session");
  const session = await checked(() => supabase.auth.getSession(), assertCurrent);
  if (session.error || session.data.session?.user.id !== ownerId || !session.data.session.access_token) throw new TaskExecutorError("invalid_session");
  return session.data.session;
}
export async function invokeTaskAction(body: TaskRequest, ownerId: string, assertCurrent: TaskOwnerGuard = noGuard): Promise<TaskResponse> {
  const session = await taskOwnerSession(ownerId, assertCurrent);
  const { supabase } = await checked(() => import("@/integrations/supabase/client"), assertCurrent);
  const response = await checked(() => supabase.functions.invoke("task-executor", { body, headers: { Authorization: "Bearer " + session.access_token } }), assertCurrent);
  await taskOwnerSession(ownerId, assertCurrent);
  if (response.error) {
    const context = (response.error as { context?: Response }).context;
    if (context && typeof context.clone === "function") {
      try { const payload = await checked(() => context.clone().json(), assertCurrent); if (typeof payload?.error === "string") throw new TaskExecutorError(payload.error, context, payload.safe_retry === true, isConfirmedTaskFailure(body as unknown as Record<string, unknown>, payload)); }
      catch (cause) { if (cause instanceof TaskExecutorError) throw cause; }
    }
    throw new TaskExecutorError("outcome_unknown", context);
  }
  if (typeof response.data?.error === "string") throw new TaskExecutorError(response.data.error, undefined, response.data.safe_retry === true, isConfirmedTaskFailure(body as unknown as Record<string, unknown>, response.data));
  if (!object(response.data) || response.data.ok !== true || response.data.action !== body.action) throw new TaskExecutorError("outcome_unknown");
  if (TASK_WRITE_ACTIONS.includes(body.action as TaskWriteAction) && !hasConfirmedTaskActionResult(body as unknown as Record<string, unknown>, response.data, ownerId)) throw new TaskExecutorError("outcome_unknown");
  return response.data as TaskResponse;
}

export const readPendingTaskAction = (ownerId: string) => loadConnectorWrite(ownerId, "local_task");
/** Standalone module writes share the durable journal with reviewed captures. */
export async function executeReviewedTaskAction(ownerId: string, request: TaskRequest, assertCurrent: TaskOwnerGuard = noGuard) {
  await taskOwnerSession(ownerId, assertCurrent);
  const body = { ...request, idempotency_key: taskOperationKey(String(request.operation_id), request.action) } as ConnectorWriteBody;
  const claim = await checked(() => claimConnectorWrite(ownerId, "local_task", body, { title: typeof request.title === "string" ? request.title : undefined }, undefined, assertCurrent), assertCurrent);
  if (JSON.stringify(claim.entry.body) !== JSON.stringify(body)) throw new TaskExecutorError("task_pending_action");
  if (claim.entry.body.mirror_context) throw new TaskExecutorError("task_pending_action");
  try {
    let response: TaskResponse;
    if (claim.entry.status === "succeeded") {
      const lookup = await invokeTaskAction({ action: "receipt", operation_id: String(body.operation_id) }, ownerId, assertCurrent);
      if (!hasConfirmedTaskReceiptResult(body, lookup, ownerId)) throw new TaskExecutorError("outcome_unknown");
      response = { ...lookup, ok: true, action: body.action, operation_id: String(body.operation_id), task_id: String(body.task_id) };
    } else response = await invokeTaskAction(claim.entry.body as unknown as TaskRequest, ownerId, assertCurrent);
    await checked(() => finishConnectorWrite(claim.entry, false, assertCurrent), assertCurrent);
    const entry = { ...claim.entry, status: "succeeded" as const, confirmedResult: response };
    return { entry, response };
  } catch (cause) {
    if (claim.entry.status === "pending" && cause instanceof TaskExecutorError && cause.safe_retry && (claim.created || cause.confirmed_failed)) await checked(() => finishConnectorWrite(claim.entry, true, assertCurrent), assertCurrent);
    throw cause;
  }
}
export async function acknowledgeTaskAction(entry: PendingConnectorWrite, assertCurrent: TaskOwnerGuard = noGuard) {
  await taskOwnerSession(entry.ownerId, assertCurrent);
  if (entry.group !== "local_task" || entry.status !== "succeeded" || entry.body.mirror_context) throw new TaskExecutorError("task_pending_action");
  await checked(() => finishConnectorWrite(entry, true, assertCurrent), assertCurrent);
}
export function newTaskOperationId() {
  if (typeof crypto.randomUUID === "function") return crypto.randomUUID();
  const bytes = crypto.getRandomValues(new Uint8Array(16)); bytes[6] = (bytes[6] & 15) | 64; bytes[8] = (bytes[8] & 63) | 128;
  const hex = Array.from(bytes, byte => byte.toString(16).padStart(2, "0")).join("");
  return [hex.slice(0, 8), hex.slice(8, 12), hex.slice(12, 16), hex.slice(16, 20), hex.slice(20)].join("-");
}
